import { FormSubmissionSuccess } from '../../Utils/formTracking';
import { useContainerV2 } from '../../Hooks/useContainerV2';
import PlaceholderRenderer from './PlaceholderRenderer';
import './registerModuleComponents';
import './ContainerRendererV2.scss';
import { ContainerData, ModuleClickAction } from '../../Types/containerV2';
import { mapStyleToVarsAndClasses } from '../../Utils/containerUtils';
import { ContainerContextProvider, InlineTextEditorConfig } from './ContainerContext';
import { ObjectType } from '../../Types/base';
import { bizLink } from '../../Types/bizLink';

type ContainerRendererV2Props = {
	container?: ContainerData;
	containerId?: number;
	objectType?: ObjectType;
	objectId?: number;
	clickHandler?: (link: bizLink) => void;
	/** Enables finish-state tracking and completion reporting. Disable in admin previews. */
	trackUserProgress?: boolean;
	/** When set, only the listed action types will fire on module click. Omit to allow all. */
	enabledClickActions?: ModuleClickAction['type'][];
	/** Optional admin-only inline editor hook for text modules. */
	inlineTextEditor?: InlineTextEditorConfig;
	/** Render media without playback controls while selecting modules in the editor. */
	isEditing?: boolean;
	passwordPreview?: 'locked' | 'unlocked';
	onFormSubmissionSuccess?: (event: FormSubmissionSuccess) => void;
};

/**
 * Renders a container by ID using the V2 data contract.
 * Fetches the container from the backend, normalizes it (if legacy format),
 * and renders the placeholder/module hierarchy.
 */
const ContainerRendererV2 = ({
	container,
	containerId,
	objectType,
	objectId,
	clickHandler,
	trackUserProgress = true,
	enabledClickActions,
	inlineTextEditor,
	isEditing,
	passwordPreview,
	onFormSubmissionSuccess,
}: ContainerRendererV2Props) => {
	const { container: fetchedContainer, statusState, errorMessage } = useContainerV2(containerId);

	const effectiveContainer = container ?? fetchedContainer;

	if (statusState === 'LOADING') {
		return <div className='biz-container-v2 biz-container-v2--loading'>Loading...</div>;
	}

	if (statusState === 'ERROR') {
		return <div className='biz-container-v2 biz-container-v2--error'>{errorMessage ?? 'Failed to load container.'}</div>;
	}

	if (!effectiveContainer || effectiveContainer.placeholders.length === 0) {
		return null;
	}

	const { vars, classes } = mapStyleToVarsAndClasses(effectiveContainer.style, effectiveContainer.mobileStyle);
	classes.unshift('biz-container-v2'); // Ensure base class is first for easier overrides

	return (
		<ContainerContextProvider
			container={effectiveContainer}
			objectType={objectType}
			objectId={objectId}
			clickHandler={clickHandler}
			trackUserProgress={trackUserProgress}
			enabledClickActions={enabledClickActions}
			inlineTextEditor={inlineTextEditor}
			isEditing={isEditing}
			passwordPreview={passwordPreview}
			onFormSubmissionSuccess={onFormSubmissionSuccess}
		>
			<div className={classes.join(' ')} data-container-id={effectiveContainer.id} style={vars}>
				{effectiveContainer.placeholders.map((placeholder) => (
					<PlaceholderRenderer key={placeholder.id} placeholder={placeholder} />
				))}
			</div>
		</ContainerContextProvider>
	);
};

export default ContainerRendererV2;
